/**
 * poiArtifactHost — единый менеджер одного WebGLRenderer'а на artifact-сцены.
 *
 * Обслуживает и preview-карточки в чате, и full-screen overlay. Canvas
 * физически «переезжает» между container'ами через `api.resume(newContainer)`,
 * не создавая новый WebGL-контекст. Одновременно у canvas один владелец
 * (`claimantId`) — либо `'fullscreen'`, либо `cardId` карточки.
 *
 * Инвариант: **максимум 2 WebGL-контекста на всё приложение** (splat + один
 * artifact). Никогда больше, независимо от того сколько карточек в чате.
 *
 * Смена главной сцены (`$activeSceneId`) → `disposePoiArtifactHost()`.
 */

import { createGlbScene, type GlbSceneApi } from './createGlbScene';
import type { GlbSceneConfig } from './glbSceneConfig';
import { $activeSceneId } from '../../state/sceneNavigation';
import { logEvent } from '../../debug/memoryTracker';

interface CachedHost {
  /**
   * `string` — content загружен для этой sceneId.
   * `null` — shell в standby-режиме (renderer/scene/camera жив, но content
   * выгружен через `api.standby()`). Следующий claim должен загрузить новый
   * content через `api.swapContent(config)`.
   */
  sceneId: string | null;
  api: GlbSceneApi;
}

// --- DEBUG helpers (no-op when ?mem-debug=1 not set because logEvent is gated) ---
const describeEl = (el: HTMLElement | null | undefined): Record<string, unknown> => {
  if (!el) return { present: false };
  return {
    present: true,
    tag: el.tagName,
    cls: (el.className ?? '').toString().slice(0, 40),
    connected: el.isConnected,
    size: `${el.clientWidth}x${el.clientHeight}`,
  };
};

const describeCanvas = (api: GlbSceneApi | null): Record<string, unknown> => {
  if (!api) return { present: false };
  const canvas = api.renderer.domElement;
  return {
    present: true,
    connected: canvas.isConnected,
    parentCls: (canvas.parentElement?.className ?? 'null').toString().slice(0, 40),
    drawSize: `${canvas.width}x${canvas.height}`,
    styleSize: `${canvas.style.width}x${canvas.style.height}`,
  };
};

let cached: CachedHost | null = null;
/**
 * `true` когда GLB-модель загружена и `createGlbScene.onReady` отработал.
 * Используется `App.tsx` для пропуска прелоадера при открытии full-screen
 * поверх уже загруженного preview.
 */
let cachedReady = false;
/**
 * Колбэк, который надо вызвать когда `cachedReady` станет true. Обновляется при
 * каждом claim'е пока модель ещё не готова — чтобы onReady сработал у текущего
 * владельца, даже если он сменился во время загрузки.
 */
let pendingOnReady: ((api: GlbSceneApi) => void) | null = null;
let activeClaimantId: string | null = null;
let activeSceneWatchInstalled = false;
/**
 * Generation counter для swapContent. При mismatch sceneId в Case B вместо
 * dispose+recreate мы вызываем `cached.api.swapContent` — если пользователь
 * быстро переключается между третьими-четвёртыми карточками, поздние onReady
 * от отменённых swap'ов не должны стрелять по текущему владельцу.
 */
let swapGeneration = 0;

const ensureActiveSceneWatch = () => {
  if (activeSceneWatchInstalled) return;
  activeSceneWatchInstalled = true;
  let lastActiveId = $activeSceneId.getState();
  $activeSceneId.watch((id) => {
    if (id === lastActiveId) return;
    const prev = lastActiveId;
    lastActiveId = id;
    // Artifact семантически привязан к родительской основной сцене.
    // При любой смене активной сцены (в другую сцену или в global-map,
    // id === null) диспозим cached, чтобы освободить GPU-память.
    if (cached) {
      logEvent('poiArtifactHost: dispose on active scene change', {
        prevSceneId: prev,
        newActiveSceneId: id,
        cachedSceneId: cached.sceneId,
      });
      disposePoiArtifactHost();
    }
  });
};

export interface ClaimPoiArtifactOptions {
  /** Уникальный идентификатор претендента: `'fullscreen'` или `cardId` карточки. */
  claimantId: string;
  container: HTMLElement;
  config: GlbSceneConfig;
  /** Включить zoom в OrbitControls. Для preview-карточек `false`, для full-screen `true`. */
  enableZoom?: boolean;
  /**
   * Вызывается когда сцена готова. `api` передаётся параметром (без TDZ при
   * синхронном вызове внутри `const api = claimPoiArtifact(...)`).
   */
  onReady?: (api: GlbSceneApi) => void;
}

/**
 * Получить в собственность artifact-canvas. Поведение зависит от состояния:
 * - тот же `sceneId` в cached → `api.resume(container)` (без перезагрузки).
 * - другой `sceneId` → полный teardown + create new.
 * - нет cached → create new.
 *
 * onReady:
 * - Если `cachedReady` уже `true` (модель была загружена при предыдущем
 *   claim'е) — вызывается **синхронно**.
 * - Иначе — сохраняется в `pendingOnReady` и вызывается когда модель
 *   загрузится. Если claimant сменится до того как модель загрузится,
 *   `pendingOnReady` перезаписывается — onReady получит текущий владелец.
 */
export const claimPoiArtifact = ({
  claimantId,
  container,
  config,
  enableZoom = true,
  onReady: onReadyCb,
}: ClaimPoiArtifactOptions): GlbSceneApi => {
  ensureActiveSceneWatch();

  const sceneId = config.id;

  // Case A: cached совпадает по sceneId — переиспользуем
  if (cached && cached.sceneId === sceneId) {
    logEvent('poiArtifactHost: reuse cached (before resume)', {
      sceneId,
      claimantId,
      previousClaimant: activeClaimantId,
      cachedReady,
      container: describeEl(container),
      canvas: describeCanvas(cached.api),
    });
    cached.api.controls.enableZoom = enableZoom;
    cached.api.resume(container);
    activeClaimantId = claimantId;
    logEvent('poiArtifactHost: reuse cached (after resume)', {
      sceneId,
      claimantId,
      cachedReady,
      container: describeEl(container),
      canvas: describeCanvas(cached.api),
    });
    if (cachedReady) {
      // Модель загружена — onReady синхронно (без TDZ, т.к. api передаётся параметром)
      onReadyCb?.(cached.api);
      pendingOnReady = null;
      logEvent('poiArtifactHost: onReady fired sync', { sceneId, claimantId });
    } else {
      // Ещё грузится — запоминаем колбэк для текущего владельца
      pendingOnReady = onReadyCb ?? null;
    }
    return cached.api;
  }

  // Case B: cached shell есть, но content неподходящий. Два подпути:
  //   - `cached.sceneId === null` (standby после release) → shell пустой,
  //     swapContent внутри сделает unload no-op + load нового. Минимальный
  //     GPU-пик, никакого overlap старого и нового content.
  //   - `cached.sceneId !== sceneId` (другой живой artifact) → классический
  //     swap: unload + load. Редкий путь — у нас один claimant 'fullscreen',
  //     close всегда срабатывает перед open'ом другого.
  // В обоих случаях shell (renderer, scene, camera, controls) переиспользуется.
  if (cached) {
    const gen = ++swapGeneration;
    const swapMode = cached.sceneId === null ? 'reload (shell standby)' : 'swap (content mismatch)';
    logEvent(`poiArtifactHost: ${swapMode}`, {
      oldSceneId: cached.sceneId,
      newSceneId: sceneId,
      claimantId,
      previousClaimant: activeClaimantId,
      gen,
    });
    cached.api.controls.enableZoom = enableZoom;
    cached.api.resume(container);
    cached.sceneId = sceneId;
    cachedReady = false;
    pendingOnReady = onReadyCb ?? null;
    activeClaimantId = claimantId;
    cached.api.swapContent(config, () => {
      if (gen !== swapGeneration) {
        logEvent('poiArtifactHost: swap onReady STALE', { sceneId, gen, currentGen: swapGeneration });
        return;
      }
      if (!cached || cached.sceneId !== sceneId) {
        logEvent('poiArtifactHost: swap onReady cache mismatch', {
          sceneId,
          cachedSceneId: cached?.sceneId ?? null,
        });
        return;
      }
      cachedReady = true;
      logEvent('poiArtifactHost: swap onReady fired', { sceneId, claimantId: activeClaimantId });
      pendingOnReady?.(cached.api);
      pendingOnReady = null;
    });
    return cached.api;
  }

  // Case C: создаём новый
  logEvent('poiArtifactHost: create new', {
    sceneId,
    claimantId,
    container: describeEl(container),
  });
  pendingOnReady = onReadyCb ?? null;
  // Override enableZoom через копию config
  const effectiveConfig: GlbSceneConfig = {
    ...config,
    controls: { ...config.controls, enableZoom },
  };
  const api = createGlbScene({
    container,
    config: effectiveConfig,
    sights: [],
    suppressLoadingEvents: true,
    ignoreViewOffset: true,
    useContainerSize: true,
    onReady: () => {
      if (cached?.sceneId === sceneId) {
        cachedReady = true;
        logEvent('poiArtifactHost: createGlbScene onReady', {
          sceneId,
          hasPendingCb: pendingOnReady !== null,
          activeClaimantId,
          canvas: describeCanvas(cached.api),
        });
        pendingOnReady?.(cached.api);
        pendingOnReady = null;
      } else {
        logEvent('poiArtifactHost: createGlbScene onReady STALE', {
          sceneId,
          cachedSceneId: cached?.sceneId ?? null,
        });
      }
    },
  });
  cached = { sceneId, api };
  activeClaimantId = claimantId;
  return api;
};

/**
 * Освободить artifact — полностью выгружаем content модели из GPU, но оставляем
 * shell (renderer, scene, camera, controls, lighting, envMap) живым через
 * `api.standby()`. Это радикально снижает GPU-использование (~90% веса артефакта
 * приходится на текстуры content) и убирает memory-pike при следующем open
 * другого артефакта — новая модель загружается на уже освобождённой GPU.
 *
 * После standby `cached.sceneId` становится `null` — следующий `claimPoiArtifact`
 * сделает `swapContent(newConfig)` поверх пустого shell (unload уже сделан,
 * просто load). Никакого `forceContextLoss`, никакого нового `WebGLRenderer`.
 */
export const releasePoiArtifact = (claimantId: string): void => {
  if (!cached) {
    logEvent('poiArtifactHost: release NOOP (no cached)', { claimantId });
    return;
  }
  if (activeClaimantId !== claimantId) {
    logEvent('poiArtifactHost: release NOOP (claimant mismatch)', {
      askedClaimantId: claimantId,
      activeClaimantId,
      sceneId: cached.sceneId,
    });
    return;
  }
  logEvent('poiArtifactHost: release → standby (before)', {
    sceneId: cached.sceneId,
    claimantId,
    canvas: describeCanvas(cached.api),
  });
  // Standby: выгружаем content (модели + текстуры + mixers + collision + poi),
  // pause render loop, снимаем canvas с DOM. Shell остаётся.
  cached.api.standby();
  cached.sceneId = null;
  cachedReady = false;
  pendingOnReady = null;
  activeClaimantId = null;
  // Инвалидируем in-flight swap'ы — их поздние onReady не должны сработать
  // на новом claim с загрузкой другой модели.
  swapGeneration++;
  logEvent('poiArtifactHost: release → standby (after)', {
    claimantId,
    canvas: describeCanvas(cached.api),
  });
};

/**
 * Полный teardown: dispose + forceContextLoss + удаление canvas из DOM.
 * Вызывается автоматически при смене `$activeSceneId` или явно при unmount app.
 */
export const disposePoiArtifactHost = (): void => {
  if (!cached) return;
  logEvent('poiArtifactHost: disposeHost', { sceneId: cached.sceneId });
  cached.api.dispose();
  cached = null;
  cachedReady = false;
  pendingOnReady = null;
  activeClaimantId = null;
  // Инвалидируем любые in-flight swap'ы — их поздние onReady уже не попадут
  // в actual cached (он null).
  swapGeneration++;
};

/**
 * «Зрелый» cached для указанной sceneId — модель загружена и готова к показу.
 * Используется в `App.tsx` для пропуска прелоадера full-screen при открытии
 * поверх уже загруженного preview.
 */
export const isPoiArtifactCached = (sceneId: string): boolean =>
  cachedReady && cached !== null && cached.sceneId === sceneId;
